import { useCallback, useMemo } from "react";
import { PermissionType } from "../../../../../dtos/PermissionType";
import { useAppContext } from "../../../../shared/AppContext";
import { phaseNameShownInPhaseHeader } from "../../../../shared/Types";
import { checkPhasePermission, deriveStepStyleTaskStatus, getPhaseStatusInfo, isStepStyleWorkflow } from "../../../../shared/Utils";
import { useWorkflowTypeDescriptions } from "../../../../tasklist/useWorkflowTypeInfo";
import { useTaskListContext } from "../../context/TaskListContext";
import { ITaskStatusIconProps } from "../TaskStatusIcon";

export function useTaskStatusIconController({
    phase,
    steps,
    onStatusIconClick,
    textOnly,
    grantData,
    collapsed,
    onToggleCollapsed,
    subtaskCount
}: ITaskStatusIconProps) {
    const app = useAppContext();
    const { tasklistMode } = useTaskListContext();
    const workflowDescriptions = useWorkflowTypeDescriptions();

    const canViewSummary = useMemo(
        () => {
            if (!phase) return false;
            // Always allow click in templates mode
            if (tasklistMode === "templates") return true;
            return checkPhasePermission(phase, app, PermissionType.View, true, grantData?.id);
        },
        [phase, app, grantData, tasklistMode]
    );

    // Every task shows a status: the stored one, for Step-style tasks one derived from their
    // subtasks, and for upload-only tasks one read from the task's own dates.
    const wfType = phase?.defaultPhase?.settings?.workflowType;

    const nameShownInPhaseHeader = phaseNameShownInPhaseHeader(phase, tasklistMode === "templates");

    const statusInfo = useMemo(() => {
        if (!phase) return null;

        if (tasklistMode !== "templates") {
            // Step-style tasks (Step, Step Hybrid) have no task-level workflow, so the stored
            // phase status only tracks the coarse transitions. Derive the indicator from the
            // subtasks in view instead. Templates have no real subtask statuses, so they keep the
            // phase status.
            if (isStepStyleWorkflow(wfType)) {
                // Not loaded yet - show nothing rather than a status the subtasks may contradict.
                if (steps == null) return null;
                return getPhaseStatusInfo(deriveStepStyleTaskStatus(steps), phase.dueDate, wfType);
            }
        }

        // The task overall, which is its newest cycle. Every cycle sits under this one header,
        // so opening a prior cycle does not change what the header reports.
        return getPhaseStatusInfo(phase.status, phase.dueDate, wfType);
    }, [phase, steps, wfType, tasklistMode]);

    const handleClick = useCallback(() => {
        if (!phase) return;
        if (!canViewSummary) return;
        onStatusIconClick?.();
    }, [canViewSummary, onStatusIconClick, phase]);

    const cursor = canViewSummary ? "pointer" : "default";
    const phaseName = phase?.name ?? "";
    const dueDateText = phase?.dueDate ? phase.dueDate.toDateString() : "";

    return {
        isVisible: !!phase && !nameShownInPhaseHeader,
        workflowDescription: wfType != null ? workflowDescriptions.get(wfType) ?? null : null,
        statusInfo,
        handleClick,
        cursor,
        phaseName,
        dueDateText,
        textOnly,
        workflowType: wfType,
        mode: tasklistMode,
        collapsed,
        onToggleCollapsed,
        subtaskCount
    };
}
